import {
  BulkActionsContext,
  Button,
  Flex,
  Form,
  FormErrorTypes,
  InputAutocomplete,
  Modal,
  ModalContext,
  Text,
  toast,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
} from 'dash-ui';

import { FormEvent, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetPortfoliosDocument,
  GetPortfolioFolderItemsDocument,
  GetPortfoliosTreeDocument,
  useGetAllPortfolioFoldersQuery,
  useAddPropertiesToPortfolioMutation,
  useCreatePortfolioFolderMutation,
  GetAllPortfolioFoldersDocument,
  CurrentBrokerHasPortfoliosDocument,
  useDeletePortfolioByIdMutation,
} from 'gql/generated.graphql';

import { useTheme } from 'styled-components';
import {
  DashValidation,
  ServerErrors,
  isNameLengthValid,
  or,
} from 'dash-common';

enum FormFields {
  FolderName = 'folderName',
  CreatedFolderId = 'createdFolderId',
}

const MovePropertyToFolderModal = () => {
  const theme = useTheme();
  const { t } = useTranslation(['common', 'pages']);
  const { isOpenCurrent, toggleModal, payload } = useContext(ModalContext);
  const { resetState } = useContext(BulkActionsContext);
  const isOpen = isOpenCurrent['MovePropertyToFolderModal'];
  const [formState, [setFolderName, setCreatedFolderId]] = useFormState([
    FormFields.FolderName,
    FormFields.CreatedFolderId,
  ]);
  const [formValidation, , [setFolderNameError]] = useFormValidation([
    FormFields.FolderName,
  ]);
  const formRef = useRef<HTMLFormElement | null>(null);

  const [createPortfolioFolder, { loading: loadingCreate }] =
    useCreatePortfolioFolderMutation({
      refetchQueries: [
        GetPortfoliosDocument,
        GetAllPortfolioFoldersDocument,
        CurrentBrokerHasPortfoliosDocument,
      ],
    });

  const [deleteProperty] = useDeletePortfolioByIdMutation({
    refetchQueries: [
      GetPortfoliosDocument,
      CurrentBrokerHasPortfoliosDocument,
      GetAllPortfolioFoldersDocument,
      GetPortfolioFolderItemsDocument,
    ],
  });

  const { data } = useGetAllPortfolioFoldersQuery({
    fetchPolicy: 'network-only',
  });
  const portfolioFolders = useMemo(
    () => data?.getAllPortfolioFolders || [],
    [data]
  );
  const isFolderExist = !!portfolioFolders.find(
    (item) => item.name === formState.folderName
  );

  const [addPropertiesToPortfolio, { loading: isLoading }] =
    useAddPropertiesToPortfolioMutation({
      refetchQueries: [GetPortfolioFolderItemsDocument],
    });
  const translateErrorMessage = useServerResponseTranslation();

  const closeModal = () => {
    toggleModal(false, 'MovePropertyToFolderModal');
    setFolderName('');
    setCreatedFolderId('');
    setFolderNameError(null);
    resetState();
  };

  const handleInputValue = (value: string) => {
    setFolderName(value);
    if (!value) {
      return setFolderNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.nameBlank'),
      });
    }
    if (!isNameLengthValid(value as string)) {
      return setFolderNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
    }
    setFolderNameError(null);
  };

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const portfolioFolderId = isFolderExist
      ? portfolioFolders.find(({ name }) => name === formState.folderName)?._id
      : formState.createdFolderId;
    const { selectedItems } = payload;
    const propertyIds = selectedItems.map((item) => item.property._id);
    const portfolioIds = selectedItems.map((item) => item._id);
    addPropertiesToPortfolio({
      variables: {
        propertyIds,
        portfolioFolderId,
      },
      refetchQueries: [
        'GetPortfolioFolderItems',
        CurrentBrokerHasPortfoliosDocument,
        {
          query: GetPortfoliosDocument,
          variables: {
            pagination: {
              limit: 10,
            },
          },
          fetchPolicy: 'network-only',
        },
        {
          query: GetPortfoliosTreeDocument,
          fetchPolicy: 'network-only',
        },
        {
          query: GetPortfolioFolderItemsDocument,
          variables: {
            portfolioFolderId,
          },
        },
      ],
      onCompleted: () => {
        portfolioIds.forEach((portfolioId) => {
          deleteProperty({
            variables: {
              portfolioId,
            },
            refetchQueries: [
              {
                query: GetPortfoliosTreeDocument,
                fetchPolicy: 'network-only',
              },
              {
                query: GetAllPortfolioFoldersDocument,
                fetchPolicy: 'network-only',
              },
              {
                query: GetPortfolioFolderItemsDocument,
                variables: {
                  portfolioFolderId,
                },
              },
            ],
          });
        });
        closeModal();
        toast.success(t('pages:portfoliosPage.toasts.propertyWasAdded'));
      },
      onError: (error) => {
        if (error.message === ServerErrors.PORTFOLIO_CONTAIN_FOLDER) {
          return toast.error(
            t('pages:portfoliosPage.toasts.propertyAlreadyAdded')
          );
        }
        toast.error(translateErrorMessage(error));
      },
    });
  };

  const createFolder = () => {
    if (!isFolderExist) {
      createPortfolioFolder({
        variables: {
          name: formState.folderName,
        },
        onCompleted: (data) => {
          setCreatedFolderId(data.createPortfolioFolder._id);
          toast.success(t('pages:portfoliosPage.toasts.portfolioIsCreated'));
        },
        onError: (error) => toast.error(translateErrorMessage(error)),
      });
    }
  };

  const foldersOptions = useMemo(() => {
    return portfolioFolders.map((item) => {
      const isDisabled = item.portfolioPropertiesIds?.some((id) =>
        payload?.selectedItems?.map((si) => si.property._id)?.includes(id)
      );

      return {
        value: item.name || '',
        label: item.name || '',
        isDisabled,
      };
    });
  }, [portfolioFolders, payload]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      title={t('pages:portfoliosPage.addPropertyToPortfolioModalTitle') || ''}
      submitButton={{
        title: t('common:labels.add'),
        isLoading,
        disabled: or(!isFolderExist, isLoading),
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t('common:labels.cancel'),
        onClick: closeModal,
      }}
    >
      <Form ref={formRef} onSubmit={onFormSubmit}>
        <Text color={theme.colors.n80}>
          {t('pages:portfoliosPage.addPropertyToPortfolioModalText') || ''}
        </Text>
        <Form.Group mt={3} mb={1}>
          <Form.Label>{t('forms:placeholders.folderName')}</Form.Label>
          <Flex>
            <InputAutocomplete
              w="100%"
              maxWidth={[200, 300, 300, 300]}
              placeholder={t('forms:placeholders.enterFolderName') || ''}
              onChange={handleInputValue}
              options={foldersOptions}
              $errorType={formValidation.folderName?.type}
            />
            <Button
              p={2}
              minWidth={72}
              type="button"
              ml={2}
              variation="outline"
              onClick={createFolder}
              disabled={!formState.folderName || isFolderExist}
              isLoading={loadingCreate}
            >
              {t('common:labels.create')}
            </Button>
          </Flex>
          <Form.Feedback $errorType={formValidation.folderName?.type}>
            {formValidation.folderName?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default MovePropertyToFolderModal;
